
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { getUserFamilyId } from './helpers';
import { Memory } from '@/types/memory';
import { logger } from '@/utils/logger';

// Milestones storage functions
export interface MilestoneItem {
  id: string;
  content: string;
  childId: string;
  childName: string;
  date: string;
  context?: string;
}

export const getStoredMilestones = async (): Promise<MilestoneItem[]> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      return [];
    }
    
    const familyId = await getUserFamilyId();
    if (!familyId) return [];
    
    // Need to use two separate queries since join syntax errors
    const { data: milestones, error: milestonesError } = await supabase
      .from('milestones')
      .select('*')
      .eq('family_id', familyId)
      .order('date', { ascending: false });

    if (milestonesError) {
      logger.error('Error fetching milestones:', milestonesError);
      return [];
    }

    // Early return if no milestones
    if (!milestones || milestones.length === 0) return [];

    // Now get child names for all milestones
    const childIds = [...new Set(milestones.map(milestone => milestone.child_id))];
    const { data: children, error: childrenError } = await supabase
      .from('children')
      .select('id, name')
      .in('id', childIds);

    if (childrenError) {
      logger.error('Error fetching children for milestones:', childrenError);
      return [];
    }

    if (!children) return [];

    // Create a map of child id to name for quick lookup
    const childMap = new Map();
    children.forEach(child => {
      childMap.set(child.id, child.name);
    });

    return milestones.map(milestone => ({
      id: milestone.id,
      content: milestone.content,
      childId: milestone.child_id,
      childName: childMap.get(milestone.child_id) || 'Unknown Child',
      date: milestone.date,
      context: milestone.context
    }));
  } catch (error) {
    logger.error('Error in getStoredMilestones:', error);
    return [];
  }
};

export const storeMilestones = async (milestone: Omit<MilestoneItem, 'id' | 'childName'>): Promise<MilestoneItem | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return null;
    }
    
    const familyId = await getUserFamilyId();
    if (!familyId) {
      logger.error('User has no family');
      return null;
    }
    
    // First get the child's name
    const { data: childData, error: childError } = await supabase
      .from('children')
      .select('name')
      .eq('id', milestone.childId)
      .single();

    if (childError) {
      logger.error('Error fetching child name:', childError);
      return null;
    }

    const { data, error } = await supabase
      .from('milestones')
      .insert({
        content: milestone.content,
        child_id: milestone.childId,
        date: milestone.date,
        context: milestone.context,
        user_id: userData.user.id,
        family_id: familyId
      })
      .select()
      .single();

    if (error) {
      logger.error('Error storing milestone:', error);
      return null;
    }

    if (!data || !childData) return null;

    return {
      id: data.id,
      content: data.content,
      childId: data.child_id,
      childName: childData.name,
      date: data.date,
      context: data.context
    };
  } catch (error) {
    logger.error('Error in storeMilestones:', error);
    return null;
  }
};

// Update milestone function
export const updateMilestone = async (milestone: MilestoneItem): Promise<MilestoneItem | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return null;
    }
    
    const { data, error } = await supabase
      .from('milestones')
      .update({
        content: milestone.content,
        child_id: milestone.childId,
        date: milestone.date,
        context: milestone.context
      })
      .eq('id', milestone.id)
      .eq('user_id', userData.user.id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating milestone:', error);
      return null;
    }

    if (!data) return null;

    // Get the child name
    const { data: childData, error: childError } = await supabase
      .from('children')
      .select('name')
      .eq('id', data.child_id)
      .single();

    if (childError || !childData) {
      logger.error('Error fetching child name:', childError);
      return null;
    }

    return {
      id: data.id,
      content: data.content,
      childId: data.child_id,
      childName: childData.name,
      date: data.date,
      context: data.context
    };
  } catch (error) {
    logger.error('Error in updateMilestone:', error);
    return null;
  }
};

// Convert to Memory type
export const milestoneToMemory = (milestone: MilestoneItem): Memory => ({
  id: milestone.id,
  content: milestone.content,
  childName: milestone.childName,
  childId: milestone.childId,
  date: milestone.date,
  type: 'milestone',
  context: milestone.context
});
